/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react';
import Layout from '@/components/layout/Index';
import ContactHeader from '@/components/sections/ContactHeader';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { ArrowRightCircle } from 'lucide-react';
import { sendToHubSpot } from '@/utils/hubspot';
import { getContactPage } from '@/sanity/client';
import Loader from '@/components/ui/Loader';

const formSchema = z.object({
	name: z.string().min(2, {
		message: 'Name must be at least 2 characters.',
	}),
	email: z.string().email({
		message: 'Please enter a valid email address.',
	}),
	company: z.string().optional(),
	contactReason: z.string({
		required_error: 'Please select a reason for contact.',
	}),
	message: z.string().min(10, {
		message: 'Message must be at least 10 characters.',
	}),
});

const Contact = () => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSuccess, setIsSuccess] = useState(false);
	const [contactData, setContactData] = useState<any>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const fetchContactData = async () => {
			try {
				const pageData = await getContactPage();
				if (pageData) {
					setContactData(pageData);
				}
			} catch (error) {
				console.error('Error fetching contact page data:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchContactData();
	}, []);

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			name: '',
			email: '',
			company: '',
			contactReason: '',
			message: '',
		},
	});

	async function onSubmit(values: z.infer<typeof formSchema>) {
		setIsSubmitting(true);

		try {
			await sendToHubSpot(
				{
					properties: [
						{ property: 'firstname', value: values.name },
						{ property: 'email', value: values.email },
						{ property: 'company', value: values.company },
						{ property: 'contact_reason', value: values.contactReason },
						{ property: 'message', value: values.message },
					],
				},
				5, // Replace with the appropriate HubSpot list ID for contact requests
			);

			// Call the Netlify function to send emails
			const baseurl =
				process.env.NODE_ENV === 'production'
					? 'https://yaap.co.za/.netlify/functions'
					: '/.netlify/functions';

			// Send confirmation email to the user
			await fetch(`${baseurl}/email-service`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					type: 'sendEmail',
					to: values.email,
					subject: 'Contact Request Received',
					body: `
            <p>Dear ${values.name},</p>
            <p>Thank you for contacting us. We have received your message and will get back to you shortly.</p>
            <p>Best regards,<br>Yaap Team</p>
          `,
				}),
			});

			// Send alert email to the admin
			await fetch(`${baseurl}/email-service`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					type: 'sendEmail',
					to: 'bbndaba@yaap.co.za',
					cc: 'info@yaap.co.za',
					subject: 'Yaap Website: New Contact Request',
					body: `
            <p>A new contact request has been submitted:</p>
            <p><strong>Name:</strong> ${values.name}</p>
            <p><strong>Email:</strong> ${values.email}</p>
            <p><strong>Company:</strong> ${values.company}</p>
            <p><strong>Reason for Contact:</strong> ${values.contactReason}</p>
            <p><strong>Message:</strong> ${values.message}</p>
          `,
				}),
			});

			setIsSuccess(true);
			form.reset();
		} catch (error) {
			console.error('Failed to submit form:', error);
		} finally {
			setIsSubmitting(false);
		}
	}

	if (loading) {
		return <Loader />;
	}

	return (
		<div className='flex flex-col w-full overflow-hidden'>
			<Layout>
				<main className='w-full'>
					<ContactHeader />

					<section className='py-12 md:px-34 bg-background rounded-t-3xl relative z-20 -mt-5'>
						<div className='max-w-7xl md:px-15 px-10  '>
							<div className='mb-10 max-w-7xl'>
								<h2 className='text-3xl font-bold mb-4'>
									{contactData?.contactFormTitle || 'Get in touch'}
								</h2>
								<p className='text-muted-foreground'>
									{contactData?.contactFormDescription ||
										"Whether you're interested in learning more about our services, " +
											'scheduling a consultation, or just want to say hello, feel ' +
											'free to reach out.'}
								</p>
							</div>

							<div className='grid grid-cols-1 lg:grid-cols-12 gap-8 items-start'>
								<div className='lg:col-span-7'>
									{isSuccess ? (
										<div className='bg-green-50 border border-green-200 p-6 rounded-lg text-center'>
											<h3 className='text-xl font-semibold text-green-700 mb-2'>
												Message sent successfully!
											</h3>
											<p className='text-green-600 mb-4'>
												Thank you for contacting us. We'll respond shortly.
											</p>
											<Button
												onClick={() => setIsSuccess(false)}
												variant='outline'
											>
												Send another message
											</Button>
										</div>
									) : (
										<Form {...form}>
											<form
												onSubmit={form.handleSubmit(onSubmit)}
												className='space-y-6'
											>
												<div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
													<FormField
														control={form.control}
														name='name'
														render={({ field }) => (
															<FormItem>
																<FormLabel>Name</FormLabel>
																<FormControl>
																	<Input placeholder='Your name' {...field} />
																</FormControl>
																<FormMessage />
															</FormItem>
														)}
													/>

													<FormField
														control={form.control}
														name='email'
														render={({ field }) => (
															<FormItem>
																<FormLabel>Email</FormLabel>
																<FormControl>
																	<Input
																		placeholder='your.email@example.com'
																		{...field}
																	/>
																</FormControl>
																<FormMessage />
															</FormItem>
														)}
													/>
												</div>

												<div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
													<FormField
														control={form.control}
														name='company'
														render={({ field }) => (
															<FormItem>
																<FormLabel>Company (Optional)</FormLabel>
																<FormControl>
																	<Input
																		placeholder='Your company name'
																		{...field}
																	/>
																</FormControl>
																<FormMessage />
															</FormItem>
														)}
													/>

													<FormField
														control={form.control}
														name='contactReason'
														render={({ field }) => (
															<FormItem>
																<FormLabel>Reason for Contact</FormLabel>
																<Select
																	onValueChange={field.onChange}
																	defaultValue={field.value}
																>
																	<FormControl>
																		<SelectTrigger>
																			<SelectValue placeholder='Select a reason' />
																		</SelectTrigger>
																	</FormControl>
																	<SelectContent>
																		<SelectItem value='personal'>
																			Personal Coaching
																		</SelectItem>
																		<SelectItem value='corporate'>
																			Corporate Services
																		</SelectItem>
																		<SelectItem value='general'>
																			General Inquiry
																		</SelectItem>
																	</SelectContent>
																</Select>
																<FormMessage />
															</FormItem>
														)}
													/>
												</div>

												<FormField
													control={form.control}
													name='message'
													render={({ field }) => (
														<FormItem>
															<FormLabel>How can we help you?</FormLabel>
															<FormControl>
																<Textarea
																	placeholder="Let us know how we can help you. We'll get back to you shortly."
																	className='min-h-[150px]'
																	{...field}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>

												<Button
													type='submit'
													className='w-full md:w-auto bg-[#fbb040] hover:bg-amber-300 rounded-full'
													disabled={isSubmitting}
												>
													{isSubmitting ? 'Sending...' : 'Send Message'}
													<ArrowRightCircle className='w-6 h-6 ml-2' />
												</Button>
											</form>
										</Form>
									)}
								</div>

								<div className='lg:col-span-5'>
									<div className=' p-6 rounded-lg h-full'>
										<h3 className='text-xl font-semibold mb-4'>
											Contact Details
										</h3>
										<div className='space-y-4'>
											<div>
												<p className='font-medium'>Email</p>
												<a
													href={`mailto:${contactData?.contactDetails?.email || 'info@yaap.co.za'}`}
													className='text-amber-600 hover:underline'
												>
													{contactData?.contactDetails?.email ||
														'info@yaap.co.za'}
												</a>
											</div>
											<div>
												<p className='font-medium'>Phone</p>
												<a
													href={`tel:${contactData?.contactDetails?.phone || ''}`}
													className='text-amber-600 hover:underline'
												>
													{contactData?.contactDetails?.phone || ''}
												</a>
											</div>

											<div>
												<p className='font-medium'>Office Hours</p>
												{contactData?.contactDetails?.officeHours ? (
													contactData.contactDetails.officeHours.map(
														(hours: string, index: number) => (
															<p key={index}>{hours}</p>
														),
													)
												) : (
													<>
														<p>Monday - Friday: 9AM - 5PM</p>
														<p>Weekend: Closed</p>
													</>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</section>
				</main>
			</Layout>
		</div>
	);
};

export default Contact;
